import { useState, useRef, useEffect } from "react";
import api from "../../lib/api";
import { Card, Overline, Btn, Icon } from "../../components/ui-kit";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

export default function CurriculumPage() {
  const [form, setForm] = useState({ board: "CBSE", class_name: "Class 10", subject: "Physics", language: "English", depth: "standard", instructions: "" });
  const [job, setJob] = useState(null);
  const [stages, setStages] = useState([]);
  const [running, setRunning] = useState(false);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const start = async () => {
    setRunning(true); setJob(null);
    try {
      const { data } = await api.post("/curriculum/generate", form);
      setStages(data.stages);
      poll.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/curriculum/job/${data.job_id}`);
          setJob(j);
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(poll.current); setRunning(false);
            if (j.status === "completed") toast.success("Curriculum generated!");
            else toast.error("Generation failed. You can retry.");
          }
        } catch (err) { console.error("Curriculum poll failed:", err); }
      }, 1200);
    } catch (e) {
      setRunning(false); toast.error("Could not start generation.");
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue outline-none text-sm bg-white";
  const done = job?.completed_stages || [];

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-mim-blue/10 flex items-center justify-center"><Icon name="Wand2" className="w-6 h-6 text-mim-blue" /></div>
        <div><Overline>One-Click Generation</Overline><h1 className="text-2xl font-bold text-mim-navy">AI Curriculum Factory</h1></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Board"><input value={form.board} onChange={(e)=>setForm({...form,board:e.target.value})} className={inputCls} /></F>
            <F label="Class"><input value={form.class_name} onChange={(e)=>setForm({...form,class_name:e.target.value})} className={inputCls} /></F>
          </div>
          <F label="Subject"><input data-testid="curr-subject" value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})} className={inputCls} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Language"><input value={form.language} onChange={(e)=>setForm({...form,language:e.target.value})} className={inputCls} /></F>
            <F label="Content depth">
              <select value={form.depth} onChange={(e)=>setForm({...form,depth:e.target.value})} className={inputCls}>
                <option value="light">Light</option><option value="standard">Standard</option><option value="deep">In-depth</option>
              </select>
            </F>
          </div>
          <F label="Additional instructions"><textarea rows={3} value={form.instructions} onChange={(e)=>setForm({...form,instructions:e.target.value})} placeholder="e.g. Emphasise numerical problem solving" className={cn(inputCls,"resize-none")} /></F>
          <Btn onClick={start} disabled={running} className="w-full" icon={running ? "LoaderCircle" : "Sparkles"} data-testid="curr-generate">
            <span className={running ? "animate-pulse" : ""}>{running ? "Generating…" : "AI Generate Curriculum"}</span>
          </Btn>
        </Card>

        <Card>
          <Overline>Generation Progress</Overline>
          {!job && !running && <p className="text-sm text-mim-muted mt-3">Configure and start to build a full curriculum with chapters, topics, lessons, questions, quizzes and worksheets.</p>}
          {(running || job) && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-semibold text-mim-navy">{job?.status === "completed" ? "Curriculum ready 🎉" : "Building your curriculum…"}</span>
                <span className="text-mim-blue font-bold">{job?.progress || 0}%</span>
              </div>
              <div className="w-full bg-[#E8EEF6] rounded-full h-2 mb-4 overflow-hidden">
                <div className="h-full bg-mim-blue rounded-full transition-all duration-500" style={{ width: `${job?.progress || 0}%` }} />
              </div>
              <div className="space-y-2">
                {stages.map((st) => {
                  const isDone = done.includes(st);
                  const isCurrent = job?.current_stage === st && !isDone;
                  return (
                    <div key={st} className="flex items-center gap-2.5 text-sm">
                      <div className={cn("w-5 h-5 rounded-full flex items-center justify-center shrink-0",
                        isDone ? "bg-mim-green text-white" : isCurrent ? "bg-mim-blue/15 text-mim-blue" : "bg-slate-100 text-mim-muted")}>
                        {isDone ? <Icon name="Check" className="w-3 h-3" /> : isCurrent ? <Icon name="LoaderCircle" className="w-3 h-3 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                      </div>
                      <span className={cn(isDone ? "text-mim-navy font-medium" : "text-mim-muted")}>{st} {isDone && "created"}</span>
                    </div>
                  );
                })}
              </div>
              {job?.status === "failed" && (
                <div className="mt-4"><Btn variant="coral" onClick={start} icon="RefreshCw">Retry</Btn></div>
              )}
              {job?.status === "completed" && job?.result?.structure?.chapters && (
                <div className="mt-4 pt-4 border-t border-mim-line">
                  <div className="text-sm font-semibold text-mim-navy mb-2 flex items-center gap-1.5"><Icon name="CircleCheck" className="w-4 h-4 text-mim-green" />Generated structure</div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {job.result.structure.chapters.map((c, i) => (
                      <div key={i} className="text-sm">
                        <div className="font-semibold text-mim-navy">{i + 1}. {c.name}</div>
                        <div className="text-xs text-mim-muted pl-4">{(c.topics || []).join(" · ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold text-mim-navy block mb-1.5">{label}</label>{children}</div>;
}
